/**
 * The instant half of a story (D-071, D-099).
 *
 * Everything here is built from public/roster.json alone — no model call, no
 * network — so the Matchup and every Facts page paint the moment the kid taps
 * Fight, while the model writes the battle behind them. The story is not needed
 * until the battle door.
 *
 * What it deliberately CANNOT produce: the Weapon and Armor edges, which come
 * from combat scores the client never receives, and everything from the battle
 * onward. Those arrive with the real story. The winner therefore stays
 * underivable on the client (invariant 1).
 *
 * The prose is the same templating `buildFallbackStory` uses, precomputed per
 * animal at build time — and since D-099 the server writes exactly these fields
 * into every story, model or fallback, so a preview page and the story's page
 * are identical. `npm run preview:check` pins that on every pair × age.
 */

import { eraNote } from '../../server/era_note.js'
import { article } from '../../server/names.js'

/**
 * Where the fight happens. Ported from `chooseSetting` in server/matchup_rules.js
 * and pinned to it by `npm run preview:check`, which compares all 16,110 pairs.
 */
export function chooseSetting(a, b) {
  const da = a.preview.domains
  const db = b.preview.domains
  const shared = ['land', 'water', 'air'].filter((d) => da.includes(d) && db.includes(d))
  if (shared.length) return shared[0]

  const constrained = da.length <= db.length ? a : b
  const other = constrained === a ? b : a
  if (constrained.preview.domains.includes('water') && !other.preview.domains.includes('water')) return 'water'
  if (other.preview.domains.includes('water') && !constrained.preview.domains.includes('water')) return 'water'
  return constrained.preview.domains[0]
}

/** Matches SETTING_PLACE in server/fallback_story.js. */
function settingPlace(setting, a, b) {
  if (setting === 'water') {
    const w = a.preview.domains.includes('water') ? a : b
    return `Warm, clear water over ${article(w.preview.habitat)} ${w.preview.habitat}`
  }
  if (setting === 'air') return 'The open sky above a rocky cliff'
  return `A wide open stretch of ${a.preview.habitat}`
}

const num = (x, y) => (x === y ? 'tie' : x > y ? 'a' : 'b')

/**
 * A partial story: enough for the Matchup and the whole Facts screen.
 * `stat_comparison` rows whose edge needs a combat score carry `edge: null`.
 * `roster` is the parsed public/roster.json: its `stat_rows` and `fact_pages`
 * blocks carry the row order and per-age counts as data.
 */
export function buildPreview({ a: A, b: B, age, roster }) {
  const { labels, by_age, combat_edge } = roster.stat_rows
  const perAnimal = roster.fact_pages.per_animal_by_age[age]
  const setting = chooseSetting(A, B)
  const edges = {
    Weight: num(A.preview.nums.weight_kg, B.preview.nums.weight_kg),
    Speed: num(A.preview.nums.kph, B.preview.nums.kph),
    Length: num(A.preview.nums.length_cm, B.preview.nums.length_cm),
  }

  return {
    preview: true,
    schema_version: 1,
    age_band: age,
    title: `${A.name} vs. ${B.name}`,
    intro: age === '2' ? `${A.name} and ${B.name}!` : `${A.name} meets ${B.name}. Only one of them walks away.`,
    setting: settingPlace(setting, A, B),
    stat_comparison: labels.slice(0, by_age[age]).map((label) => ({
      label,
      a: A.preview.cells[label],
      b: B.preview.cells[label],
      edge: combat_edge.includes(label) ? null : edges[label],
    })),
    // Pages alternate a, b, a, b — the same shape buildFallbackStory produces.
    fact_pages: Array.from({ length: 2 * perAnimal }, (_, i) => ({
      animal: i % 2 === 0 ? 'a' : 'b',
      pose: 'neutral',
      text: (i % 2 === 0 ? A : B).preview.facts[age][Math.floor(i / 2)],
    })),
    // One module with the server (server/era_note.js), so a storybook animal's
    // note (D-113) cannot drift between the preview and the story.
    cross_era_note: eraNote(A, B),
  }
}

/**
 * Fill the preview's missing edges from the real story once it lands.
 *
 * Only the highlight changes — every cell keeps the preview's wording, so no
 * text can mutate under a kid who is already reading it.
 */
export function withStoryEdges(preview, story) {
  if (!story) return preview
  const byLabel = new Map(story.stat_comparison.map((r) => [r.label, r.edge]))
  return {
    ...preview,
    stat_comparison: preview.stat_comparison.map((r) => (r.edge === null ? { ...r, edge: byLabel.get(r.label) ?? 'tie' } : r)),
  }
}
